import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ProductTilesListContext from '@stone/scripts/contexts/ProductTilesListContext';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import { compileListName } from '@stone/utils/analytics';
import { getRandomUUID } from '@common/utils/stringUtils';
import ProductTile from '@stone/components/product/ProductTile';
import ProductTilesRailNavigation from './ProductTilesRailNavigation';
import useProductsList from '@stone/hooks/useProductsList';
import customEventBus from '@common/scripts/libs/eventBus';
const ProductTilesCarousel = React.lazy(() => import('@stone/components/product/ProductTilesCarousel'));
const LatestCarousel = React.lazy(() => import('../lastest/LatestCarousel'));
const ProductTilesRail = (props) => {
    // ANCHOR - HOOKS --------------------------------------------------------------------
    const { isMobile } = useMediaQueries();
    const { t } = useTranslation();
    // ANCHOR - USE REF --------------------------------------------------------------------
    const railContainer = useRef(null);
    // ANCHOR - USE STATE --------------------------------------------------------------------
    const [UUID] = useState(props.uuid || getRandomUUID());
    // ANCHOR - USE MEMO --------------------------------------------------------------------
    const isCarousel = useMemo(() => {
        return props.railType == 'CAROUSEL' || (props.railType == 'MOBILECAROUSEL' && isMobile) || props.railType == 'LATEST';
    }, [props.railType]);
    // const [products, setProducts] = useState<commonModels.ProductTile[]>([]);
    const ListContext = useContext(ProductTilesListContext);
    const railContextValue = useMemo(() => {
        if (!ListContext) {
            return {
                analyticsData: {
                    item_list_name: compileListName(props.analyticsName || (props.railType == 'LATEST' && 'the latest') || 'editorial'),
                },
            };
        }
        return ListContext;
    }, []);
    const { products, getProducts, resetProducts, parseCategory, title, recomName } = useProductsList(railContextValue.analyticsData.item_list_name, props.limit, props.product, ListContext === null || ListContext === void 0 ? void 0 : ListContext.productList, {
        noTrackInps: props.railType === 'EDITORIALTILE',
    });
    const category = useMemo(() => {
        return parseCategory(props);
    }, [props.productTiles]);
    //check if context already exists
    const railClass = () => {
        return classNames({
            'grid-container--3tiles': props.railType == 'EDITORIALTILE',
            'grid-container': props.railType !== 'EDITORIALTILE',
        });
    };
    const wrapperClass = () => {
        return classNames(props.classname || 'container no-overflow-container', 'productTiles-rail', {
            latest_carousel: props.railType == 'LATEST',
            'editorial-rail': props.railType == 'EDITORIALTILE',
            'mobilecarousel-rail': props.railType == 'MOBILECAROUSEL',
            'carousel-rail': props.railType == 'CAROUSEL',
            'hasheader-rail': hasHeader,
        });
    };
    const customImage = (product) => {
        if (!product.assetOverride)
            return null;
        if (props.railType === 'EDITORIALTILE') {
            return product.assetOverride.plpeditorial;
        }
        if (props.pageType === 'PLP') {
            return product.assetOverride.plp;
        }
    };
    const railTitle = useMemo(() => {
        return props.title || title;
    }, [props.title, title]);
    // products fetch effect
    // priority rule
    // productsMultiline -> category -> recommenderId
    useEffect(() => {
        if (category) {
            getProducts(category);
            customEventBus.on('refresh:recommender', (args) => {
                if (args.handle === props.slot || (Array.isArray(args.handle) && args.handle.includes(props.slot))) {
                    getProducts(category);
                }
            });
        }
        return () => {
            resetProducts();
            customEventBus.off('refresh:recommender', () => { });
        };
    }, [category, props.slot]);
    const generateKey = (data, i) => {
        return (props.keyGenerator && props.keyGenerator(data, i)) || data.uuid || i;
    };
    const hasHeader = !!railTitle || !!props.url || (!!props.autoplay && props.railType === 'LATEST') || props.forceHeader;
    const renderRail = useCallback(() => {
        if (props.railType == 'CAROUSEL' || (props.railType == 'MOBILECAROUSEL' && isMobile)) {
            return (React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                React.createElement(ProductTilesCarousel, { products: products, headingLevel: (railTitle && 'h3') || 'h2', tileType: props.tileType || 'RECS', pageType: props.pageType, recommenderId: props === null || props === void 0 ? void 0 : props.recommenderId, wishlistShareEnabled: props.wishlistShareEnabled, uuid: UUID })));
        }
        if (props.railType == 'LATEST') {
            return (React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                React.createElement(LatestCarousel, { uuid: UUID, products: products })));
        }
        return (React.createElement("ul", { className: railClass() }, products === null || products === void 0 ? void 0 : products.map((product, i) => {
            var _a;
            return (React.createElement("li", { key: generateKey(product, i) },
                React.createElement(ProductTile, { wishlist: ((_a = props.wishlist) === null || _a === void 0 ? void 0 : _a.length) && props.wishlist, updateWishlist: props.updateWishlist, type: props.tileType || 'RECS', headingLevel: (railTitle && 'h3') || 'h2', pageType: props.pageType, recommender: props === null || props === void 0 ? void 0 : props.recommenderId, product: product, index: i, wishlistShareEnabled: props.wishlistShareEnabled, inPublicWishlist: props.inPublicWishlist, fullExpanded: !!props.wishlist, customImage: customImage(product) })));
        })));
    }, [products]);
    if (!(products === null || products === void 0 ? void 0 : products.length))
        return React.createElement(React.Fragment, null);
    return (React.createElement("div", { className: wrapperClass(), ref: railContainer },
        hasHeader && (React.createElement("div", null,
            React.createElement("div", { className: "rail-topbar row" },
                React.createElement("div", { className: "col-10 col-lg-16 rail-title" },
                    React.createElement("h2", { className: "font__title", tabIndex: -1 }, railTitle)),
                !isMobile && (React.createElement("div", { className: classNames('col-lg-fill rail-links', { 'text-right': !isCarousel }) }, props.url && (React.createElement("a", { className: "button button--tertiary", "aria-label": props.urlAriaLabel, href: props.url },
                    React.createElement("span", null, props.urlText))))),
                isCarousel && (React.createElement("div", { className: "col-14 col-lg-4" },
                    React.createElement(ProductTilesRailNavigation, { autoplay: props.autoplay, uuid: UUID })))))),
        (products === null || products === void 0 ? void 0 : products.length) === 0 ? (React.createElement(React.Fragment, null, t('label.loading'))) : !ListContext ? (React.createElement(ProductTilesListContext.Provider, { value: railContextValue }, renderRail())) : ((recomName && React.createElement("section", { "data-recommender": recomName }, renderRail())) || renderRail()),
        isMobile && (React.createElement("div", { className: "rail-links" }, props.url && (React.createElement("a", { className: "button button--tertiary", "aria-label": props.urlAriaLabel, href: props.url },
            React.createElement("span", null, props.urlText)))))));
};
ProductTilesRail.defaultProps = {
    railType: 'STANDARD',
};
export default ProductTilesRail;
